import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { fmtIDR, RATE_META } from "@/lib_app/api";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Line, ComposedChart, Cell } from "recharts";
import { AlertTriangle, TrendingUp, Wallet, Target as TargetIcon, Users, Trophy, ChevronDown, ChevronRight, Cake, PartyPopper, Building2, Sparkles, FolderOpen, Coins, Timer } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import Avatar from "@/components/app/Avatar";

const now = new Date();
const currentYear = now.getFullYear();
const YEARS = [currentYear - 1, currentYear, currentYear + 1];
const MONTH_LABEL = ["Jan","Feb","Mar","Apr","Mei","Jun","Jul","Agu","Sep","Okt","Nov","Des"];

export default function Dashboard() {
  const [year, setYear] = useState(currentYear);
  const [data, setData] = useState(null);
  const [rankSalesFilter, setRankSalesFilter] = useState("all");
  const [expanded, setExpanded] = useState({});
  const [birthdayHidden, setBirthdayHidden] = useState(() => {
    const key = `sp_bd_${new Date().toISOString().slice(0, 10)}`;
    return localStorage.getItem(key) === "1";
  });
  const [users, setUsers] = useState([]);
  const { user } = useAuth();
  const navigate = useNavigate();

  const isOwner = user?.role === "owner";
  // Non-owner: default to Saya. Owner: only "team" mode.
  const [scope, setScope] = useState(() => (isOwner ? "team" : localStorage.getItem("sp_dash_scope") || "me"));
  // Stage distribution scope (independent, but only relevant when user is non-owner and scope=me)
  const [stageScope, setStageScope] = useState("me");
  useEffect(() => { if (!isOwner) localStorage.setItem("sp_dash_scope", scope); }, [scope, isOwner]);

  useEffect(() => {
    api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
  }, []);

  const birthdayFolks = useMemo(() => {
    // Only greet the CURRENT logged-in user on their own birthday (not everyone's birthdays).
    if (!user?.user_id) return [];
    const today = new Date();
    const mm = String(today.getMonth() + 1).padStart(2, "0");
    const dd = String(today.getDate()).padStart(2, "0");
    return users.filter(
      (u) => u.user_id === user.user_id && u.is_active !== false && u.date_of_birth && u.date_of_birth.slice(5, 10) === `${mm}-${dd}`,
    );
  }, [users, user]);

  const dismissBirthday = () => {
    const key = `sp_bd_${new Date().toISOString().slice(0, 10)}`;
    localStorage.setItem(key, "1");
    setBirthdayHidden(true);
  };

  useEffect(() => {
    api.get(`/analytics/summary?year=${year}`).then((r) => setData(r.data)).catch(() => {});
  }, [year]);

  const ranking = useMemo(() => {
    if (!data) return [];
    const active = data.ranking.filter((s) => s.is_active);
    if (rankSalesFilter === "all") return active;
    return active.filter((s) => s.user_id === rankSalesFilter);
  }, [data, rankSalesFilter]);

  // "Me" slice from ranking (non-owner) — even if user has zero data, ranking has an entry from _users_map()
  const meStats = useMemo(() => {
    if (!data || !user) return null;
    return data.ranking.find((r) => r.user_id === user.user_id) || null;
  }, [data, user]);

  if (!data) return <div className="text-[#A0A3A8] text-sm">Memuat analitik...</div>;

  // Compute stats for the active scope
  const showPersonal = !isOwner && scope === "me" && meStats;
  const stat = showPersonal
    ? {
        target: meStats.target || 0,
        achieved: meStats.realization || 0,
        pct: meStats.achievement_pct || 0,
        active_pipeline: meStats.value || 0,
        active_count: meStats.active || 0,
        won_count: meStats.won || 0,
        lost_count: meStats.lost || 0,
        prospect_count: meStats.count || 0,
        win_rate: meStats.win_rate || 0,
        monthly: meStats.monthly || [],
        by_rate: meStats.by_rate || [],
      }
    : {
        target: data.total_target || 0,
        achieved: data.total_realization || 0,
        pct: data.achievement_pct || 0,
        active_pipeline: data.active_pipeline || 0,
        active_count: data.active_count || 0,
        won_count: data.won_count || 0,
        lost_count: data.lost_count || 0,
        prospect_count: data.prospect_count || 0,
        win_rate: data.win_rate || 0,
        monthly: data.monthly || [],
        by_rate: data.by_rate || [],
      };

  const pctClamped = stat.target > 0 ? Math.min(100, Math.round(stat.achieved / stat.target * 100)) : 0;

  // Chart data: pick monthly source
  const monthlyChart = (stat.monthly || []).map((m) => {
    const tgt = m.target || 0;
    const ach = m.realization || 0;
    return {
      month: MONTH_LABEL[Number(m.month.split("-")[1]) - 1] || m.month,
      target: tgt,
      achievement: ach,
      pct: tgt > 0 ? Math.round((ach * 100) / tgt * 100) / 100 : 0,
    };
  });

  // Stage distribution: personal (from meStats.by_rate) or team (data.by_rate) — controlled by stageScope
  const useStagePersonal = !isOwner && stageScope === "me" && meStats;
  const stageSrc = useStagePersonal ? (meStats.by_rate || []) : (data.by_rate || []);
  const rateChart = stageSrc.map((r) => ({ stage: RATE_META[r.rate].label, count: r.count, value: r.value }));
  const CHART_COLORS = ["#FF6B7D", "#FFDB52", "#97ADD4"];

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Header row with scope toggle */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <p className="text-[11px] uppercase tracking-[0.24em] text-[#626061]/70 mb-1.5 font-bold">Dashboard · {year}</p>
          <h1 className="text-[40px] md:text-[44px] tracking-tight font-bold text-[#090808] leading-none">Halo, {user?.name.split(" ")[0]} 👋</h1>
          <p className="text-sm text-[#626061]/70 mt-2.5">
            {showPersonal ? "Ringkasan personal — target, pipeline, dan follow-up milikmu." : "Ringkasan target vs pencapaian seluruh tim."}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {!isOwner && (
            <div className="inline-flex bg-white/60 backdrop-blur-xl border border-white/60 rounded-full p-1 gap-1 shadow-[0_4px_12px_rgba(30,16,80,0.05)]" data-testid="dash-scope-toggle">
              <button
                onClick={() => setScope("me")}
                data-testid="dash-scope-me"
                className={`h-9 px-4 rounded-full text-xs font-semibold transition-colors ${
                  scope === "me" ? "chip-black" : "text-[#626061] hover:text-[#090808]"
                }`}
              >
                Saya
              </button>
              <button
                onClick={() => setScope("team")}
                data-testid="dash-scope-team"
                className={`h-9 px-4 rounded-full text-xs font-semibold transition-colors ${
                  scope === "team" ? "chip-black" : "text-[#626061] hover:text-[#090808]"
                }`}
              >
                Semua Tim
              </button>
            </div>
          )}
          <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
            <SelectTrigger data-testid="dash-year" className="w-40 h-11 rounded-full glass px-5"><SelectValue /></SelectTrigger>
            <SelectContent>
              {YEARS.map((y) => <SelectItem key={y} value={String(y)}>Tahun {y}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* ⚠️ Follow-up — top priority, only when non-empty */}
      {data.overdue.length > 0 && (
        <Card
          data-testid="overdue-card"
          className="p-6 border-0 rounded-3xl overflow-hidden relative"
          style={{
            background:
              "radial-gradient(600px 400px at 100% 0%, rgba(107,143,226,0.28) 0%, transparent 60%)," +
              "radial-gradient(500px 350px at 0% 100%, rgba(151,173,212,0.22) 0%, transparent 60%)," +
              "linear-gradient(135deg, rgba(255,255,255,0.78) 0%, rgba(232,240,254,0.68) 100%)",
            backdropFilter: "blur(24px) saturate(170%)",
            WebkitBackdropFilter: "blur(24px) saturate(170%)",
            border: "1px solid rgba(255,255,255,0.75)",
            boxShadow: "0 22px 46px -18px rgba(30,64,175,0.16), 0 6px 20px -10px rgba(20,22,31,0.08)",
          }}
        >
          <div className="flex items-center gap-2 mb-4">
            <div className="w-9 h-9 rounded-2xl bg-[#FFE89A] text-[#7A6633] flex items-center justify-center">
              <AlertTriangle className="w-4 h-4" strokeWidth={1.75} />
            </div>
            <div>
              <h3 className="text-[17px] font-bold text-[#090808]">Perlu Follow-up</h3>
              <span className="text-[11px] text-[#626061]/60 font-medium">
                {data.overdue.length} prospek · H+7 · 5 prioritas teratas
              </span>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 pr-1">
            {[...data.overdue].sort((a, b) => (b.days ?? 999) - (a.days ?? 999)).slice(0, 5).map((o) => {
              const m = RATE_META[o.rate];
              return (
                <button
                  key={o.id}
                  onClick={() => navigate(`/prospects?open=${o.id}`)}
                  data-testid={`overdue-row-${o.id}`}
                  className="w-full text-left flex items-center justify-between gap-3 py-2 px-3 rounded-full glass-soft hover:bg-white/75 transition-colors"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`w-8 h-8 rounded-full ${m.pill} text-white font-bold flex items-center justify-center text-xs shrink-0`}>{m.letter}</div>
                    <div className="min-w-0">
                      <div className="text-sm text-[#090808] truncate flex items-baseline gap-1.5">
                        <span className="font-semibold">{o.customer}</span>
                        {o.service && <span className="text-[11px] text-[#A0A3A8] font-normal truncate">· {o.service}</span>}
                      </div>
                      <div className="text-xs text-[#A0A3A8] truncate">{o.owner_name}</div>
                    </div>
                  </div>
                  <span className={`text-xs tabular-nums font-bold shrink-0 ${o.days == null || o.days >= 14 ? "text-[#FF6B7D]" : "text-[#B39548]"}`}>
                    {o.days == null ? "belum FU" : `${o.days}h`}
                  </span>
                </button>
              );
            })}
          </div>
          <button
            onClick={() => navigate("/notifications?type=prospect_overdue")}
            data-testid="overdue-see-all"
            className="mt-3 w-full h-10 rounded-full bg-white/70 hover:bg-white border border-white/80 text-xs font-bold text-[#090808] inline-flex items-center justify-center gap-1.5 transition-colors"
          >
            Lihat semua ({data.overdue.length}) di Pusat Notifikasi <ChevronRight className="w-3.5 h-3.5" />
          </button>
        </Card>
      )}

      {/* Birthday banner */}
      {birthdayFolks.length > 0 && !birthdayHidden && (
        <div
          data-testid="birthday-banner"
          className="glass relative overflow-hidden rounded-3xl p-5 md:p-6"
        >
          <div className="absolute -right-4 -top-4 opacity-20">
            <PartyPopper className="w-24 h-24 text-[#14161F]" strokeWidth={1.5} />
          </div>
          <div className="relative flex flex-col md:flex-row md:items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-white flex items-center justify-center shadow-sm shrink-0">
              <Cake className="w-7 h-7 text-[#14161F]" strokeWidth={1.5} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-widest text-[#626061] font-bold mb-1 font-display">Hari Ini Ulang Tahunmu</div>
              <div className="text-lg md:text-xl font-bold text-[#14161F] font-display">
                Selamat ulang tahun, {birthdayFolks.map((u) => u.nickname || u.name).join(" & ")}!
              </div>
              <div className="text-xs md:text-sm text-[#626061] mt-1">
                Semoga sehat, sukses, dan closing terus setiap hari. 🎉
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                {birthdayFolks.map((u) => (
                  <div key={u.user_id} className="inline-flex items-center gap-2 pl-1 pr-3 py-1 rounded-full bg-white/70 backdrop-blur">
                    <Avatar name={u.name} picture={u.picture} size="xs" />
                    <span className="text-xs font-semibold text-[#14161F]">{u.nickname || u.name}</span>
                  </div>
                ))}
              </div>
            </div>
            <button
              onClick={dismissBirthday}
              data-testid="birthday-dismiss"
              className="shrink-0 h-9 px-4 rounded-full text-xs font-semibold text-[#626061] bg-white/80 hover:bg-white transition-colors"
            >
              Tutup
            </button>
          </div>
        </div>
      )}

      {/* KPI row 1: Target / Achievement / % */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        <KpiCard
          testid="kpi-target"
          label={showPersonal ? "Target Saya" : "Total Target"}
          value={fmtIDR(stat.target)}
          sub={`${year}`}
          icon={TargetIcon}
          tint="bg-[#DCE0E7] text-[#4E6D9A]"
        />
        <KpiCard
          testid="kpi-achievement"
          label={showPersonal ? "Achievement Saya" : "Total Achievement"}
          value={fmtIDR(stat.achieved)}
          sub={`${stat.won_count} deal closed`}
          icon={Wallet}
          tint="bg-[#C9F0D6] text-[#57D28A]"
        />
        <KpiCard
          testid="kpi-achievement-pct"
          label={showPersonal ? "% Achievement Saya" : "% Achievement Tim"}
          value={`${(stat.pct || 0).toFixed(2)}%`}
          sub={stat.pct >= 100 ? "🎉 Target tercapai!" : `Sisa ${fmtIDR(Math.max(0, stat.target - stat.achieved))}`}
          icon={Trophy}
          tint="bg-[#FFE89A] text-[#B39548]"
        />
      </div>

      {stat.target > 0 && (
        <Card className="p-6 border-0 rounded-3xl glass">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-sm font-bold text-[#090808]">{showPersonal ? `Progress Saya Tahun ${year}` : `Progress Tim Tahun ${year}`}</div>
              <div className="text-xs text-[#A0A3A8] mt-0.5">{fmtIDR(stat.achieved)} dari {fmtIDR(stat.target)}</div>
            </div>
            <div className={`text-2xl font-bold ${stat.pct >= 100 ? "text-[#57D28A]" : stat.pct >= 60 ? "text-[#B39548]" : "text-[#626061]"}`}>{(stat.pct || 0).toFixed(2)}%</div>
          </div>
          <Progress value={pctClamped} className="h-3" />
        </Card>
      )}

      {/* Target vs Achievement Bulanan (scope-aware) */}
      <Card className="p-6 border-0 rounded-3xl glass">
        <div className="mb-5">
          <h3 className="text-lg font-bold text-[#090808]">Target vs Achievement Bulanan {showPersonal && <span className="text-xs font-medium text-[#626061] ml-1">(personal)</span>}</h3>
          <p className="text-xs text-[#A0A3A8] mt-0.5">Realisasi deal per bulan dibanding target</p>
        </div>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={monthlyChart}>
              <CartesianGrid stroke="#DCE0E7" vertical={false} />
              <XAxis dataKey="month" stroke="#A0A3A8" fontSize={11} />
              <YAxis stroke="#A0A3A8" fontSize={11} tickFormatter={(v) => (v >= 1e9 ? `${(v / 1e9).toFixed(1)}M` : v >= 1e6 ? `${(v / 1e6).toFixed(0)}Jt` : String(v))} />
              <Tooltip formatter={(v, name) => [fmtIDR(v), name]} contentStyle={{ borderRadius: 12, border: "1px solid #DCE0E7", fontSize: 12, boxShadow: "0 8px 24px rgba(0,0,0,0.06)" }} />
              <Bar dataKey="achievement" fill="#57D28A" radius={[8, 8, 0, 0]} name="Achievement" />
              <Line type="monotone" dataKey="target" stroke="#626061" strokeWidth={2.5} dot={{ r: 4, fill: "#626061" }} name="Target" strokeDasharray="5 5" />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
        <div className="grid grid-cols-6 md:grid-cols-12 gap-1.5 pt-3" data-testid="monthly-pct-strip">
          {monthlyChart.map((d, i) => (
            <div key={i} className="flex flex-col items-center rounded-lg bg-[#F4F5F7] py-1.5">
              <div className="text-[9px] uppercase text-[#A0A3A8] font-semibold">{d.month}</div>
              <div className={`text-[11px] font-bold tabular-nums ${d.pct >= 100 ? "text-[#57D28A]" : d.pct >= 60 ? "text-[#B39548]" : d.pct > 0 ? "text-[#626061]" : "text-[#A0A3A8]"}`}>
                {(d.pct || 0).toFixed(2)}%
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* KPI row 2: Pipeline / Win Rate / Sales OR Klien Baru */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <KpiCard
          testid="kpi-total-pipeline"
          label={showPersonal ? "Pipeline Saya" : "Pipeline Tim"}
          value={fmtIDR(stat.active_pipeline)}
          sub={`${stat.active_count} prospek aktif`}
          icon={TrendingUp}
          tint="bg-[#DCE0E7] text-[#4E6D9A]"
        />
        <KpiCard
          testid="kpi-total-prospek-tim"
          label={showPersonal ? "Prospek Aktif Saya" : "Prospek Aktif Tim"}
          value={stat.active_count}
          sub={`${stat.won_count} deal · ${stat.prospect_count} total`}
          icon={Users}
          tint="bg-[#DCE0E7] text-[#4E6D9A]"
        />
        <KpiCard
          testid="kpi-win-rate"
          label={showPersonal ? "Win Rate Saya" : "Win Rate Tim"}
          value={`${(stat.win_rate || 0).toFixed(2)}%`}
          sub={`${stat.won_count} won · ${stat.lost_count} lost`}
          icon={TargetIcon}
          tint="bg-pink-100 text-pink-600"
        />
        <KpiCard
          testid="kpi-new-clients"
          label="Klien Baru Minggu Ini"
          value={showPersonal ? data.new_clients_week.me : data.new_clients_week.team}
          sub={showPersonal ? `Tim: ${data.new_clients_week.team}` : `sejak ${data.new_clients_week.week_start.slice(5)}`}
          icon={Building2}
          tint="bg-teal-100 text-teal-600"
        />
      </div>

      {/* Distribusi Stage + Recent Deals */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <Card className="p-6 border-0 rounded-3xl glass">
          <div className="flex items-center justify-between mb-1">
            <h3 className="text-lg font-bold text-[#090808]">Distribusi Stage</h3>
            {!isOwner && (
              <div className="inline-flex bg-[#F4F5F7] rounded-full p-0.5 gap-0.5 text-[10px]" data-testid="stage-scope-toggle">
                <button
                  onClick={() => setStageScope("me")}
                  data-testid="stage-scope-me"
                  className={`h-6 px-2.5 rounded-full font-bold transition-colors ${stageScope === "me" ? "bg-[#14161F] text-white" : "text-[#626061]"}`}
                >Saya</button>
                <button
                  onClick={() => setStageScope("team")}
                  data-testid="stage-scope-team"
                  className={`h-6 px-2.5 rounded-full font-bold transition-colors ${stageScope === "team" ? "bg-[#14161F] text-white" : "text-[#626061]"}`}
                >Tim</button>
              </div>
            )}
          </div>
          <p className="text-xs text-[#A0A3A8] mb-4">Prospek aktif per suhu {useStagePersonal && <span className="text-[#626061]">· personal</span>}</p>
          <div className="h-52">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={rateChart}>
                <CartesianGrid stroke="#DCE0E7" vertical={false} />
                <XAxis dataKey="stage" stroke="#A0A3A8" fontSize={11} />
                <YAxis stroke="#A0A3A8" fontSize={11} />
                <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #DCE0E7", fontSize: 12 }} />
                <Bar dataKey="count" radius={[10, 10, 0, 0]}>
                  {rateChart.map((_, i) => (<Cell key={i} fill={CHART_COLORS[i]} />))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-4 space-y-2.5">
            {stageSrc.map((r, i) => (
              <div key={r.rate} className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2.5">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ background: CHART_COLORS[i] }} />
                  <span className="text-[#090808] font-medium">{RATE_META[r.rate].label}</span>
                </div>
                <span className="text-[#A0A3A8] font-num text-xs">{r.count} · {fmtIDR(r.value)}</span>
              </div>
            ))}
          </div>
        </Card>

        {/* Deal Terakhir — scope-aware */}
        <Card className="p-6 border-0 rounded-3xl glass" data-testid="recent-deals-card">
          {(() => {
            const recentSrc = showPersonal
              ? (data.recent_deals || []).filter((d) => d.owner_id === user?.user_id)
              : (data.recent_deals || []);
            return (
              <>
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 rounded-xl bg-[#C9F0D6] text-[#57D28A] flex items-center justify-center">
                    <Sparkles className="w-4 h-4" />
                  </div>
                  <h3 className="text-lg font-bold text-[#090808]">{showPersonal ? "Deal Terakhir Saya" : "Deal Terakhir Tim"}</h3>
                  <span className="text-xs text-[#A0A3A8] ml-1">({recentSrc.length})</span>
                </div>
                {recentSrc.length === 0 ? (
                  <div className="text-sm text-[#A0A3A8] text-center py-8">Belum ada deal.</div>
                ) : (
                  <div className="space-y-1 max-h-72 overflow-y-auto pr-1 -mx-2">
                    {recentSrc.map((d) => (
                      <button
                        key={d.id}
                        onClick={() => navigate(`/prospects?open=${d.id}`)}
                        data-testid={`recent-deal-${d.id}`}
                        className="w-full text-left flex items-center justify-between gap-3 py-2.5 px-3 rounded-2xl hover:bg-white/70 transition-colors"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <Avatar name={d.owner_nickname} picture={d.owner_picture} size="sm" />
                          <div className="min-w-0">
                            <div className="text-sm font-semibold text-[#14161F] truncate">{d.customer}</div>
                            <div className="text-[11px] text-[#626061] truncate">{d.owner_nickname} · {d.deal_date?.slice(0, 10)}</div>
                          </div>
                        </div>
                        <div className="text-sm font-num text-[#14161F] shrink-0">{fmtIDR(d.value)}</div>
                      </button>
                    ))}
                  </div>
                )}
              </>
            );
          })()}
        </Card>
      </div>

      {/* Cash Flow row — placed above Ranking */}
      {data.cash_flow && (
        <div data-testid="cashflow-row" className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <KpiCard
            testid="kpi-outstanding"
            label={showPersonal ? "Outstanding Saya" : "Total Outstanding Tim"}
            value={fmtIDR(showPersonal ? data.cash_flow.outstanding.me : data.cash_flow.outstanding.team)}
            sub="Sisa tagihan (termasuk proyek selesai)"
            icon={FolderOpen}
            tint="bg-[#FDE7E9] text-[#CE6968]"
            onClick={() => navigate("/projects")}
          />
          <KpiCard
            testid="kpi-paid-this-month"
            label={showPersonal ? "Terbayar Bulan Ini (Saya)" : "Terbayar Bulan Ini"}
            value={fmtIDR(showPersonal ? data.cash_flow.paid_this_month.me : data.cash_flow.paid_this_month.team)}
            sub={`sejak ${(data.cash_flow.month_start || "").slice(5)}`}
            icon={Coins}
            tint="bg-[#E4F9EC] text-[#3EB876]"
          />
          <KpiCard
            testid="kpi-projects-overdue"
            label={showPersonal ? "Proyek Overdue (Saya)" : "Proyek Overdue"}
            value={(showPersonal ? data.cash_flow.overdue_count.me : data.cash_flow.overdue_count.team) || 0}
            sub="Deadline terlewat & belum selesai"
            icon={Timer}
            tint="bg-[#E5EEFA] text-[#3B4A63]"
            onClick={() => navigate("/projects")}
          />
        </div>
      )}

      {/* Ranking Sales - unchanged */}
      <Card className="p-6 border-0 rounded-3xl glass" data-testid="ranking-card">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h3 className="text-lg font-bold text-[#090808]">Ranking Sales</h3>
            <p className="text-xs text-[#A0A3A8] mt-0.5">Klik untuk lihat target & achievement per sales</p>
          </div>
          <Select value={rankSalesFilter} onValueChange={setRankSalesFilter}>
            <SelectTrigger data-testid="rank-filter" className="w-52 h-10 bg-white border-[#DCE0E7] rounded-full px-5"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua Tim</SelectItem>
              {data.ranking.filter((s) => s.is_active).map((s) => <SelectItem key={s.user_id} value={s.user_id}>{s.nickname || s.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {ranking.length === 0 ? (
          <div className="text-sm text-[#A0A3A8]">Belum ada data.</div>
        ) : (
          <div className="space-y-2">
            {ranking.map((s, i) => {
              const isOpen = expanded[s.user_id];
              const spct = s.achievement_pct || 0;
              return (
                <div key={s.user_id} className="border border-[#DCE0E7] rounded-xl">
                  <button
                    onClick={() => setExpanded({ ...expanded, [s.user_id]: !isOpen })}
                    data-testid={`rank-row-${s.user_id}`}
                    className="w-full flex items-center gap-3 px-4 py-3 hover:bg-[#F4F5F7] transition-colors rounded-xl"
                  >
                    <div className={`w-8 h-8 rounded-xl flex items-center justify-center text-xs font-bold ${
                      i === 0 ? "bg-[#FFE89A] text-[#7A6633]" : i === 1 ? "bg-[#B9C5D8] text-[#626061]" : i === 2 ? "bg-[#FFD7B5] text-[#7A5533]" : "bg-[#F4F5F7] text-[#A0A3A8]"
                    }`}>#{i + 1}</div>
                    <Avatar name={s.name} picture={s.picture} size="sm" />
                    <div className="flex-1 min-w-0 text-left">
                      <div className="text-sm font-semibold text-[#090808] truncate flex items-center gap-2">
                        {s.nickname || s.name}
                        {!s.is_active && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#DCE0E7] text-[#626061] font-medium">Nonaktif</span>}
                      </div>
                      <div className="text-xs text-[#A0A3A8]">{s.count} prospek · {s.won} deal</div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-num text-[#14161F]">{fmtIDR(s.realization)}</div>
                      <div className="text-xs text-[#A0A3A8]">dari {fmtIDR(s.target || 0)}</div>
                    </div>
                    <div className="w-14 text-right">
                      <div className={`text-sm font-num ${spct >= 100 ? "text-[#57D28A]" : spct >= 60 ? "text-[#B39548]" : "text-[#A0A3A8]"}`}>{spct.toFixed(2)}%</div>
                    </div>
                    {isOpen ? <ChevronDown className="w-4 h-4 text-[#A0A3A8]" /> : <ChevronRight className="w-4 h-4 text-[#A0A3A8]" />}
                  </button>
                  {isOpen && (
                    <div className="px-4 pb-4 pt-1 space-y-3 border-t border-[#DCE0E7]" data-testid={`rank-detail-${s.user_id}`}>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-3">
                        <Metric label="Target Tahunan" value={fmtIDR(s.target || 0)} />
                        <Metric label="Target Bulanan" value={fmtIDR(s.monthly_target || 0)} />
                        <Metric label="Achievement" value={fmtIDR(s.realization)} />
                        <Metric label="Pipeline Aktif" value={fmtIDR(s.value)} />
                      </div>
                      <Progress value={Math.min(100, spct)} className="h-2" />
                      <SalesMonthlyChart monthly={s.monthly || []} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
}

function KpiCard({ label, value, sub, icon: Icon, tint, testid, onClick }) {
  const clickable = typeof onClick === "function";
  return (
    <Card
      data-testid={testid}
      onClick={onClick}
      className={`p-6 border-0 rounded-3xl glass ${clickable ? "cursor-pointer hover:shadow-md transition-shadow" : ""}`}
    >
      <div className="flex items-start justify-between mb-4">
        <span className="text-[10px] uppercase tracking-[0.2em] text-[#626061]/70 font-bold">{label}</span>
        <div className={`w-10 h-10 rounded-full glass-soft flex items-center justify-center ${tint}`}><Icon className="w-4 h-4" strokeWidth={1.8} /></div>
      </div>
      <div className="text-2xl font-bold text-[#090808] font-num">{value}</div>
      <div className="text-xs text-[#626061]/70 mt-1.5">{sub}</div>
    </Card>
  );
}

function Metric({ label, value, className = "" }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-[#A0A3A8] font-semibold mb-1">{label}</div>
      <div className={`text-sm font-num text-[#14161F] ${className}`}>{value}</div>
    </div>
  );
}

function SalesMonthlyChart({ monthly }) {
  const data = (monthly || []).map((m) => {
    const idx = Number((m.month || "").split("-")[1] || 0) - 1;
    return {
      month: MONTH_LABEL[idx] || m.month,
      target: m.target || 0,
      achievement: m.realization || 0,
      pct: m.pct || 0,
    };
  });
  const hasAny = data.some((d) => d.target > 0 || d.achievement > 0);
  if (!hasAny) {
    return (
      <div className="mt-4 rounded-xl border border-dashed border-[#DCE0E7] px-4 py-8 text-center text-xs text-[#A0A3A8]">
        Belum ada target atau realisasi tahun ini.
      </div>
    );
  }
  return (
    <div className="mt-4 space-y-2" data-testid="sales-monthly-chart">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-widest text-[#A0A3A8] font-semibold">Target vs Achievement Bulanan</div>
        <div className="flex items-center gap-3 text-[10px] text-[#A0A3A8]">
          <span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-[#57D28A]" /> Achievement</span>
          <span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-[#14161F]" /> Target</span>
        </div>
      </div>
      <div className="h-48">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} margin={{ top: 10, right: 8, bottom: 0, left: 0 }}>
            <CartesianGrid stroke="#DCE0E7" vertical={false} />
            <XAxis dataKey="month" stroke="#A0A3A8" fontSize={10} />
            <YAxis stroke="#A0A3A8" fontSize={10} tickFormatter={(v) => (v >= 1e9 ? `${(v / 1e9).toFixed(1)}M` : v >= 1e6 ? `${(v / 1e6).toFixed(0)}Jt` : String(v))} />
            <Tooltip
              formatter={(v, name) => [fmtIDR(v), name]}
              contentStyle={{ borderRadius: 12, border: "1px solid #DCE0E7", fontSize: 11, boxShadow: "0 8px 24px rgba(0,0,0,0.06)" }}
            />
            <Bar dataKey="achievement" fill="#57D28A" radius={[6, 6, 0, 0]} name="Achievement" />
            <Line type="monotone" dataKey="target" stroke="#626061" strokeWidth={2} dot={{ r: 3, fill: "#626061" }} strokeDasharray="4 4" name="Target" />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <div className="grid grid-cols-6 md:grid-cols-12 gap-1.5 pt-1">
        {data.map((d, i) => (
          <div key={i} className="flex flex-col items-center rounded-lg bg-[#F4F5F7] py-1.5">
            <div className="text-[9px] uppercase text-[#A0A3A8] font-semibold">{d.month}</div>
            <div className={`text-[11px] font-bold tabular-nums ${d.pct >= 100 ? "text-[#57D28A]" : d.pct >= 60 ? "text-[#B39548]" : d.pct > 0 ? "text-[#626061]" : "text-[#A0A3A8]"}`}>
              {(d.pct || 0).toFixed(2)}%
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


